<template>
  <div
    class="px-5 py-6 h-full flex flex-col overflow-y-auto"
    style="padding-bottom: max(1.5rem, env(safe-area-inset-bottom, 0))"
  >
    <div class="flex items-start mb-6">
      <Logo class="h-3" />
    </div>

    <h1 class="text-[24px] font-bold mb-6 text-text-primary leading-tight">
      {{ showOtp ? 'Verify your Number' : 'Sign In' }}
    </h1>

    <form @submit.prevent="handleSubmit" class="flex flex-col gap-6 flex-1">
      <div class="flex-1">
        <div v-if="!showOtp" class="flex flex-col gap-2">
          <MobileInput
            v-model="mobileNo"
            label="Enter Your Mobile Number"
            placeholder="Enter mobile number"
            :disabled="loading"
            :error="mobileError"
            hint="We'll send a verification code to your number."
            id="mobile-no"
            @blur="validateMobile"
            @update:model-value="handleMobileInputUpdate"
          />
        </div>

        <div v-if="showOtp" class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <p class="text-[14px] text-text-secondary text-left">
              Enter the code sent to {{ formattedMobileDisplay }} to continue.
            </p>
            <OtpInput
              v-model="otp"
              :disabled="loading"
              :error="otpError"
              id="otp"
              @blur="validateOtp"
              @update:model-value="handleOtpInputUpdate"
            />
          </div>

          <div class="text-left text-[14px] text-text-secondary">
            Resend code in {{ formattedResendTime }}
          </div>

          <Button
            type="button"
            variant="text"
            size="small"
            :disabled="loading || !canResend"
            class="text-start"
            @click="handleResendOtp"
          >
            {{ canResend ? 'Resend OTP' : '' }}
          </Button>
        </div>
      </div>

      <div class="mt-auto">
        <Button
          type="submit"
          variant="primary"
          size="default"
          :disabled="loading || !isFormValid"
          :loading="loading"
          full-width
        >
          {{ showOtp ? 'Confirm' : 'Next' }}
        </Button>

        <div v-if="!showOtp" class="text-start text-[14px] text-text-secondary mt-2">
          Don't have an account?
          <button
            type="button"
            class="text-primary no-underline hover:underline bg-transparent border-none cursor-pointer ml-1 text-[14px]"
            @click="handleSignUpClick"
          >
            Sign Up
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import Logo from '@/components/atoms/Logo.vue'
import Button from '@/components/common/Button.vue'
import MobileInput from '@/components/common/MobileInput.vue'
import OtpInput from '@/components/common/OtpInput.vue'
import type { OtpRequest } from '@/interfaces'
import { useMobileValidation } from '@/composables/features/useMobileValidation'
import { useOtpValidation } from '@/composables/features/useOtpValidation'
import { useResendTimer } from '@/composables/features/useResendTimer'
import { parseApiError, parseFieldError } from '@/utils/errorHandler'
import { useRestaurantStore } from '@/stores/restaurant'
import { reservationApi } from '@/api/reservation'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const restaurantStore = useRestaurantStore()

const {
  mobileNo,
  mobileError,
  formattedMobileNo,
  formattedMobileDisplay,
  isValid: isMobileValid,
  handleMobileInputUpdate,
  validateMobile,
  setError: setMobileError,
} = useMobileValidation()

const {
  otp,
  otpError,
  isValid: isOtpValid,
  handleOtpInputUpdate: handleOtpUpdate,
  validateOtp,
  setError: setOtpError,
} = useOtpValidation()

const { formattedResendTime, canResend, startCooldown: startResendCooldown } = useResendTimer(30)

const showOtp = ref<boolean>(false)
const loading = ref<boolean>(false)

const isFormValid = computed(() => {
  if (!showOtp.value) {
    return isMobileValid.value
  }
  return isOtpValid.value
})

const handleOtpInputUpdate = (value: string | number): void => {
  handleOtpUpdate(value)
  if (otp.value.length === 4) {
    handleVerifyOtp()
  }
}

const resolveActiveReservationForCurrentTable = async (
  fallbackTenantId: string,
  scannedTableNo?: string
): Promise<{ reservationId?: string; tenantId?: string }> => {
  const activeReservationId = authStore.activeReservationId
  if (!activeReservationId) {
    return {}
  }

  const numericId = Number(activeReservationId)
  if (Number.isNaN(numericId)) {
    return {}
  }

  try {
    const response = await reservationApi.checkActiveReservation({
      active_reservation_id: numericId,
    })

    if (response.error) {
      return {}
    }

    const reservation = response.data as Record<string, unknown> | undefined
    const resolvedReservationId =
      reservation?.active_reservation_id != null || reservation?.id != null
        ? String(reservation.active_reservation_id ?? reservation.id)
        : activeReservationId
    const resolvedTenantId =
      typeof reservation?.tenant_id === 'string' && reservation.tenant_id.trim()
        ? reservation.tenant_id
        : fallbackTenantId
    const resolvedTableNo =
      typeof reservation?.table_no === 'string' && reservation.table_no.trim()
        ? reservation.table_no
        : scannedTableNo || ''
    const reservationNumber =
      typeof reservation?.reservation_number === 'string' && reservation.reservation_number.trim()
        ? reservation.reservation_number
        : typeof reservation?.reservation_numer === 'string' && reservation.reservation_numer.trim()
          ? reservation.reservation_numer
          : undefined

    authStore.setActiveReservation(resolvedReservationId, {
      tenantId: resolvedTenantId,
      tableNo: resolvedTableNo,
      ...(reservationNumber ? { reservationNumber } : {}),
    })

    const tenantMatches =
      !fallbackTenantId || !resolvedTenantId || resolvedTenantId === fallbackTenantId
    const tableMatches = !scannedTableNo || !resolvedTableNo || resolvedTableNo === scannedTableNo

    if (!tenantMatches || !tableMatches) {
      return {}
    }

    return {
      reservationId: resolvedReservationId,
      ...(resolvedTenantId ? { tenantId: resolvedTenantId } : {}),
    }
  } catch {
    return {}
  }
}

const handleSubmit = async (): Promise<void> => {
  if (!showOtp.value) {
    await handleSendOtp()
  } else {
    await handleVerifyOtp()
  }
}

const handleSendOtp = async (): Promise<void> => {
  if (!validateMobile()) return

  loading.value = true

  try {
    const checkResponse = await authStore.checkCustomerMobile({
      mobile_no: formattedMobileNo.value,
    })

    if (checkResponse.data?.error) {
      const errorMsg = checkResponse.data.error.mobile_no?.[0] || checkResponse.data.error
      setMobileError(
        typeof errorMsg === 'string' ? errorMsg : 'Account not found. Please register first.'
      )
      return
    }

    showOtp.value = true
    startResendCooldown()
  } catch (error: unknown) {
    const fieldError = parseFieldError(error, 'mobile_no')
    setMobileError(fieldError || parseApiError(error).message)
  } finally {
    loading.value = false
  }
}

const handleVerifyOtp = async (): Promise<void> => {
  if (!validateOtp()) return

  loading.value = true

  try {
    const data: OtpRequest & { otp: string } = {
      mobile_no: formattedMobileNo.value,
      otp: otp.value,
    }

    await authStore.verifyOTP(data)

    const tableNo = route.query.table_no as string | undefined
    const tokenValidate = route.query.token_validate === 'true'
    const existingReservationId = route.query.reservation_id as string | undefined

    let tenantId =
      (route.params.tenantId as string | undefined) ||
      (route.params.tenant_id as string | undefined) ||
      restaurantStore.currentTenantId ||
      (route.query.tenant_id as string | undefined) ||
      (route.query.tenantId as string | undefined) ||
      (route.query['tenant-id'] as string | undefined)

    if (!tenantId && authStore.isAuthenticated) {
      const storedReservation = localStorage.getItem('active_reservation_info')
      if (storedReservation) {
        try {
          const reservationInfo = JSON.parse(storedReservation)
          if (reservationInfo.tenantId) {
            tenantId = reservationInfo.tenantId
          }
        } catch {
          // Ignore parse errors
        }
      }
    }

    if (tenantId) {
      restaurantStore.setCurrentTenantId(tenantId)
    }

    const isOnRestaurantMenuPage =
      route.name === 'RestaurantMenu' || route.path.includes('/restaurant-menu-view/')

    const hasReservationFlow = (tokenValidate && tableNo) || existingReservationId

    if (hasReservationFlow && authStore.isAuthenticated && authStore.user?.mobile_no) {
      let finalReservationId = existingReservationId
      let finalTenantId = tenantId || ''

      if (!finalReservationId && tokenValidate && tableNo && authStore.activeReservationId) {
        const resolvedActiveReservation = await resolveActiveReservationForCurrentTable(
          finalTenantId,
          tableNo
        )
        if (resolvedActiveReservation.reservationId) {
          finalReservationId = resolvedActiveReservation.reservationId
        }
        if (resolvedActiveReservation.tenantId) {
          finalTenantId = resolvedActiveReservation.tenantId
          restaurantStore.setCurrentTenantId(finalTenantId)
        }
        if (!resolvedActiveReservation.reservationId) {
          const hasHydratedReservationInfo = !!localStorage.getItem('active_reservation_info')
          if (!hasHydratedReservationInfo) {
            authStore.clearActiveReservation()
          }
        }
      }

      if (!finalTenantId && existingReservationId) {
        const storedReservation = localStorage.getItem('active_reservation_info')
        if (storedReservation) {
          try {
            const reservationInfo = JSON.parse(storedReservation)
            if (
              reservationInfo.reservationId === existingReservationId &&
              reservationInfo.tenantId
            ) {
              finalTenantId = reservationInfo.tenantId
              restaurantStore.setCurrentTenantId(finalTenantId)
            }
          } catch {
            // Ignore parse errors
          }
        }
      }

      if (isOnRestaurantMenuPage && finalTenantId) {
        const newQuery = { ...route.query }
        delete newQuery.drawer
        router.replace({
          path: route.path,
          query: {
            ...newQuery,
            ...(tokenValidate ? { token_validate: 'true' } : {}),
            ...(tableNo ? { table_no: tableNo } : {}),
            ...(finalReservationId ? { reservation_id: finalReservationId } : {}),
          },
        })
        return
      }

      if (finalTenantId && !isOnRestaurantMenuPage) {
        router.push({
          path: `/restaurant-menu-view/${finalTenantId}`,
          query: {
            ...(tokenValidate ? { token_validate: 'true' } : {}),
            ...(tableNo ? { table_no: tableNo } : {}),
            ...(finalReservationId ? { reservation_id: finalReservationId } : {}),
          },
        })
        return // Prevent default redirect
      } else if (!finalTenantId) {
        console.error('Cannot redirect to restaurant menu: tenant_id is missing')
        if (isOnRestaurantMenuPage) {
          const newQuery = { ...route.query }
          delete newQuery.drawer
          router.replace({
            path: route.path,
            query: newQuery,
          })
          return
        }
        return
      }
    }

    if (isOnRestaurantMenuPage) {
      const newQuery = { ...route.query }
      delete newQuery.drawer
      router.replace({
        path: route.path,
        query: newQuery,
      })
      return
    }

    const newQuery = { ...route.query }
    delete newQuery.drawer
    const redirectTo = (route.query.redirect as string) || '/'
    router.push({
      path: redirectTo,
      query: newQuery,
    })
  } catch (error: unknown) {
    const fieldError = parseFieldError(error, 'otp')
    setOtpError(fieldError || parseApiError(error).message)
  } finally {
    loading.value = false
  }
}

const handleResendOtp = async (): Promise<void> => {
  if (!canResend.value) return

  loading.value = true

  try {
    await authStore.resendOTP({
      mobile_no: formattedMobileNo.value,
    })
    startResendCooldown()
  } catch (error: unknown) {
    setOtpError(parseApiError(error).message)
  } finally {
    loading.value = false
  }
}

const handleSignUpClick = (): void => {
  const newQuery = { ...route.query }
  delete newQuery.drawer
  router.push({
    path: route.path,
    query: { ...newQuery, drawer: 'register' },
  })
}
</script>
